/**
 * Radar — one surface for everything the scanners found.
 *
 * Merges the three near-identical scan views (cross-policy conflicts,
 * requirement coverage gaps, outdated policies) behind a single segmented
 * control, with live counts. Radar is the EVIDENCE view; anything that needs
 * action also lands in the Inbox as a recommendation, so users act in one
 * place and read the detail here.
 */
import React, { useState, useEffect } from 'react';
import ConflictsView from './ConflictsView';
import CoverageView from './CoverageView';
import DriftView from './DriftView';
import RegReconView from './RegReconView';
import {
  ExclamationTriangleIcon,
  ChartBarSquareIcon,
  ClockIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';

const API = process.env.REACT_APP_API_URL;

const SEGMENTS = [
  { id: 'conflicts', label: 'Policy Conflicts', Icon: ExclamationTriangleIcon, tip: 'Spots where two of your own policies contradict each other.' },
  { id: 'regrecon',  label: 'Rules & Memos',    Icon: ScaleIcon,               tip: 'Policies that contradict current CMS final rules (RuleIQ) or HPMS memos (Mandate). Confirmed non-compliance only.' },
  { id: 'coverage',  label: 'Requirement Gaps', Icon: ChartBarSquareIcon,      tip: "Regulatory requirements your policies don't yet cover — the gaps an auditor could cite." },
  { id: 'drift',     label: 'Outdated Policies', Icon: ClockIcon,              tip: 'Policies whose underlying regulation has changed, or that are overdue for review.' },
];

export default function RadarView({ onOpenPolicy }) {
  const [segment, setSegment] = useState('conflicts');
  const [counts, setCounts] = useState({});

  useEffect(() => {
    // Light-weight badge counts so the user sees where the work is without
    // clicking through each segment.
    (async () => {
      try {
        const [c, rr, g, d] = await Promise.all([
          fetch(`${API}/api/ppm/conflicts?status=open`, { credentials: 'include' }).then(r => r.json()).catch(() => null),
          fetch(`${API}/api/ppm/reg-reconciliation?status=open`, { credentials: 'include' }).then(r => r.json()).catch(() => null),
          fetch(`${API}/api/ppm/coverage`, { credentials: 'include' }).then(r => r.json()).catch(() => null),
          fetch(`${API}/api/ppm/drift?status=open`, { credentials: 'include' }).then(r => r.json()).catch(() => null),
        ]);
        setCounts({
          conflicts: c?.conflicts?.length ?? null,
          regrecon: rr?.findings?.length ?? null,
          coverage: g?.cells ? g.cells.filter(x => x.coverage === 'missing' || x.coverage === 'partial').length : null,
          drift: d?.findings?.length ?? null,
        });
      } catch {}
    })();
  }, []);

  return (
    <div className="min-h-full">
      <div className="px-8 pt-6">
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 gap-1 flex-wrap">
          {SEGMENTS.map(({ id, label, Icon, tip }) => (
            <button
              key={id}
              onClick={() => setSegment(id)}
              title={tip}
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${
                segment === id ? 'bg-orange-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              <Icon className="w-4 h-4" strokeWidth={1.75} />
              {label}
              {Number.isFinite(counts[id]) && counts[id] > 0 && (
                <span className={`ml-0.5 text-[11px] px-1.5 py-0.5 rounded-full font-bold ${
                  segment === id ? 'bg-white/20 text-white' : 'bg-orange-100 text-orange-700'
                }`}>{counts[id]}</span>
              )}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-2">
          Radar shows what the scanners found. Anything that needs a decision also lands in the Inbox as a recommendation.
        </p>
      </div>
      {segment === 'conflicts' && <ConflictsView onOpenPolicy={onOpenPolicy} />}
      {segment === 'regrecon' && <RegReconView onOpenPolicy={onOpenPolicy} />}
      {segment === 'coverage' && <CoverageView onOpenPolicy={onOpenPolicy} />}
      {segment === 'drift' && <DriftView onOpenPolicy={onOpenPolicy} />}
    </div>
  );
}
